<script setup>
const props = defineProps({
    activity: { type: Object, required: true },
    favorited: { type: Boolean, default: false },
});
defineEmits(['toggle']);

// Builder's image field can be a plain URL string OR an object ({ src, alt }).
const imageSrc = computed(() => {
    const img = props.activity?.image;
    return (typeof img === 'string' ? img : img?.src) || '';
});
const imageAlt = computed(() => {
    const img = props.activity?.image;
    return (img && typeof img === 'object' && img.alt) || props.activity?.name || '';
});

// Location is optional in Builder (UNB-641). Trimmed here rather than trusting the
// caller, so a field left with only a space doesn't render an empty eyebrow — the
// card is mounted directly in tests and could be reused elsewhere.
const locationLabel = computed(() => (props.activity?.location || '').trim());
</script>

<template>
    <article class="bg-white rounded-[10px] overflow-clip flex flex-col pb-[40px] h-full">
        <!-- The image area keeps the ARTWORK's own proportions at every width, so
             nothing is ever cropped and it scales with the column (UNB, second pass).
             It used to be a fixed 245px tall, which made the box ratio drift with the
             column: 1.80 at a 1440 desktop, 1.23 at 1024, 1.56 on a 430 phone. Since
             object-cover fills the box, whatever the box was narrower than got eaten
             off the sides — hence text disappearing at "certain desktop widths" as
             well as on mobile.
             1095/540 is the artwork itself: all 26 activity images are exactly that,
             so the box now matches and the crop is zero everywhere.
             NOTE this is slightly wider than the Figma card (440x245 = 1.80), so the
             image area is a little shorter than the comp. The alternative is keeping
             1.80 and re-exporting the artwork with the text inset, which is a design
             call rather than a code one. -->
        <div v-if="imageSrc" class="relative w-full aspect-[1095/540] overflow-hidden">
            <img :src="imageSrc" :alt="imageAlt" loading="lazy" class="absolute inset-0 w-full h-full object-cover object-bottom" />
            <div class="absolute inset-0 pointer-events-none bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(253,46,203,0.32)_79%,rgba(130,12,167,0.64)_106%)]"></div>
        </div>

        <div class="pt-[40px] px-[40px] flex flex-col gap-[12px]">
            <div class="flex flex-col gap-[20px]">
                <!-- Location is optional in Builder (UNB-641). The whole eyebrow is
                     dropped when it's empty rather than rendered blank — with the
                     title as the only child, the wrapper's gap collapses on its own,
                     so nothing is left behind. getActivities trims the value, so a
                     stray space counts as absent too. -->
                <p v-if="locationLabel" data-test="activity-location" class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-(--dark-pink)">{{ locationLabel }}</p>
                <h3 class="font-hubspot-serif font-normal text-[30px] leading-[1.3] text-(--magenta)">{{ activity.name }}</h3>
            </div>
            <!-- richText description; may contain an inline link authored in Builder -->
            <div class="activity-card__copy font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta)" v-html="activity.description"></div>

            <!-- Optional Builder-authored CTA (UNB-603, Figma 5621:59346) — an
                 uppercase text link under the description, e.g. Braindates
                 linking out. MessLink routes internal paths through NuxtLink
                 and opens external URLs in a new tab. mt-[14px] + the wrapper's
                 gap-[12px] = the Figma 26px gap below the description.
                 Hover (UNB-655): text goes --darker-pink (#7D4566) alongside the
                 existing underline drop. -->
            <MessLink
                v-if="activity.buttonUrl"
                data-test="activity-cta"
                :href="activity.buttonUrl"
                :label="activity.buttonLabel || 'Learn More'"
                class="mt-[14px] self-start font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-(--magenta) underline decoration-dotted decoration-[8.5%] underline-offset-2 transition-colors duration-200 hover:text-(--darker-pink) hover:no-underline"
            >{{ activity.buttonLabel || 'Learn More' }}</MessLink>
        </div>

        <div class="mt-auto pt-[40px] px-[40px] w-full flex justify-start">
            <button
                type="button"
                data-test="favorite-btn"
                class="inline-flex items-center gap-[8px] rounded-[50px] border border-(--magenta) px-[14px] py-[8px] font-hubspot-serif font-normal text-[15px] tracking-[0.3px] whitespace-nowrap cursor-pointer transition-colors duration-200"
                :class="favorited ? 'bg-(--magenta) text-white' : 'bg-transparent text-(--magenta) hover:bg-(--magenta) hover:text-white'"
                :aria-pressed="favorited"
                @click="$emit('toggle')"
            >
                <svg width="13" height="12" viewBox="4 4 13 12" fill="none" aria-hidden="true" class="shrink-0">
                    <path d="M16 8.06592C16 8.94907 15.6733 9.79794 15.0903 10.4257C13.7477 11.871 12.4459 13.378 11.0527 14.7712C10.7337 15.086 10.2271 15.0746 9.92187 14.7455L5.90957 10.4257C4.69681 9.11987 4.69681 7.01197 5.90957 5.70609C7.13444 4.38765 9.12931 5.16626 10.3542 6.4847L10.4999 6.6418L10.6457 6.4847C11.2331 5.85233 13.3402 3.99463 15.0903 5.70609C15.6733 6.33389 16 7.1822 16 8.06592Z" stroke="currentColor" stroke-linejoin="round"/>
                </svg>
                {{ favorited ? 'Added to My Favorites' : 'Add to My Favorites' }}
            </button>
        </div>
    </article>
</template>

<style scoped>
.activity-card__copy :deep(a) {
    text-decoration: underline dotted;
    text-decoration-color: var(--dark-pink);
    text-underline-offset: 2px;
}
</style>
